// Dashboard.js
import React, { useState, useEffect } from 'react';
import { authHelpers } from '../utils/apiService';
import UserDashboard from '../components/dashboard/UserDashboard';
import AdminDashboard from '../components/dashboard/AdminDashboard';
import SuperAdminDashboard from '../components/dashboard/SuperAdminDashboard';
import Sidebar from '../components/common/Sidebar';
import Header from '../components/common/Header';

const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    useEffect(() => {
        const userData = authHelpers.getCurrentUser();
        if (userData) {
            setUser(userData);
            // Set default tab based on user role
            if (userData.role === 'user') {
                setActiveTab('verify');
            } else {
                setActiveTab('overview');
            }
        }
        setLoading(false);
    }, []);

    const handleLogout = () => {
        authHelpers.logout();
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="loading-spinner"></div>
                <span className="ml-3">Loading dashboard...</span>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="text-center">
                    <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
                    <p className="text-gray-600 mb-4">Please log in to access the dashboard.</p>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="btn btn-primary"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    const renderDashboard = () => {
        switch (user.role) {
            case 'super_admin':
                return (
                    <SuperAdminDashboard
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        user={user}
                    />
                );
            case 'admin':
                return (
                    <AdminDashboard
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        user={user}
                    />
                );
            case 'user':
            default:
                return (
                    <UserDashboard
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        user={user}
                    />
                );
        }
    };

    return (
        <div className="dashboard">
            <Sidebar
                user={user}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isOpen={sidebarOpen}
                onToggle={toggleSidebar}
                onLogout={handleLogout}
            />

            <div className={`main-content ${!sidebarOpen ? 'full-width' : ''}`}>
                <Header
                    user={user}
                    onToggleSidebar={toggleSidebar}
                    onLogout={handleLogout}
                />

                <div className="dashboard-content">
                    {renderDashboard()}
                </div>
            </div>

            {/* Dashboard Layout Styles */}
            <style jsx>{`
                .dashboard {
                    display: flex;
                    min-height: 100vh;
                    background-color: var(--gray-50, #f9fafb);
                }

                .main-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    margin-left: 250px;
                    transition: margin-left 0.3s ease;
                    min-height: 100vh;
                }

                .main-content.full-width {
                    margin-left: 70px;
                }

                .dashboard-content {
                    flex: 1;
                    padding: 2rem;
                    overflow-y: auto;
                    background-color: var(--gray-50, #f9fafb);
                    min-height: calc(100vh - 80px); /* Adjust based on header height */
                }

                /* Loading spinner styles */
                .loading-spinner {
                    width: 2rem;
                    height: 2rem;
                    border: 3px solid var(--gray-200, #e5e7eb);
                    border-top: 3px solid var(--primary-blue, #2563eb);
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                /* Button styles */
                .btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.5rem 1rem;
                    font-size: 0.875rem;
                    font-weight: 500;
                    border-radius: 0.375rem;
                    border: 1px solid transparent;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-decoration: none;
                }

                .btn-primary {
                    background-color: var(--primary-blue, #2563eb);
                    color: white;
                    border-color: var(--primary-blue, #2563eb);
                }

                .btn-primary:hover {
                    background-color: var(--primary-blue-hover, #1d4ed8);
                    border-color: var(--primary-blue-hover, #1d4ed8);
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .main-content {
                        margin-left: 0;
                    }

                    .main-content.full-width {
                        margin-left: 0;
                    }

                    .dashboard-content {
                        padding: 1rem;
                    }
                }

                @media (min-width: 769px) and (max-width: 1024px) {
                    .dashboard-content {
                        padding: 1.5rem;
                    }
                }

                /* Utility classes for text alignment and spacing */
                .flex {
                    display: flex;
                }

                .justify-center {
                    justify-content: center;
                }

                .items-center {
                    align-items: center;
                }

                .min-h-screen {
                    min-height: 100vh;
                }

                .text-center {
                    text-align: center;
                }

                .text-xl {
                    font-size: 1.25rem;
                }

                .font-semibold {
                    font-weight: 600;
                }

                .mb-2 {
                    margin-bottom: 0.5rem;
                }

                .mb-4 {
                    margin-bottom: 1rem;
                }

                .ml-3 {
                    margin-left: 0.75rem;
                }

                .text-gray-600 {
                    color: var(--gray-600, #4b5563);
                }

                /* CSS Variables (define these in your main CSS file if not already defined) */
                :root {
                    --primary-blue: #2563eb;
                    --primary-blue-hover: #1d4ed8;
                    --secondary-blue: #3b82f6;
                    --gray-50: #f9fafb;
                    --gray-100: #f3f4f6;
                    --gray-200: #e5e7eb;
                    --gray-300: #d1d5db;
                    --gray-400: #9ca3af;
                    --gray-500: #6b7280;
                    --gray-600: #4b5563;
                    --gray-700: #374151;
                    --gray-800: #1f2937;
                    --gray-900: #111827;
                    --error-red: #dc2626;
                    --red-50: #fef2f2;
                }
            `}</style>
        </div>
    );
};

export default Dashboard;